import {
  APPROVAL_INSTANCE_STATUSES,
  APPROVAL_STEP_TYPES,
  APPROVER_STATUSES,
  type ApprovalInstanceStatus,
  type ApproverStatus,
} from '@clockwise/shared';
import { z } from 'zod';
import { ApproverSourceSchema } from '../approval-policies/types';

// Re-export the shared enums so callers don't need a separate
// `@clockwise/shared` import just for the constants.
export {
  APPROVAL_INSTANCE_STATUSES,
  APPROVER_STATUSES,
  type ApprovalInstanceStatus,
  type ApproverStatus,
};

export const ApprovalInstanceSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  policyId: z.string().uuid().nullable(),
  objectType: z.string(),
  objectId: z.string().uuid(),
  requestId: z.string().uuid().nullable(),
  status: z.enum(APPROVAL_INSTANCE_STATUSES),
  currentStepId: z.string().uuid().nullable(),
  submittedBy: z.string().uuid(),
  context: z.record(z.string(), z.unknown()),
  previousInstanceId: z.string().uuid().nullable(),
  resolutionReason: z.string().nullable(),
  // True when the matched policy had `autoApprove = true` and this
  // instance was created already-approved with no step/approver rows.
  // The matched `policyId` is still populated; UI renders
  // "Auto-approved by policy {policy.name}" when this is set.
  autoApproved: z.boolean(),
  resolvedAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type ApprovalInstance = z.infer<typeof ApprovalInstanceSchema>;

export const ApprovalInstanceStepSchema = z.object({
  id: z.string().uuid(),
  instanceId: z.string().uuid(),
  stepOrder: z.number().int(),
  approvalType: z.enum(APPROVAL_STEP_TYPES),
  approverSources: z.array(ApproverSourceSchema),
  activatedAt: z.string().nullable(),
  completedAt: z.string().nullable(),
  createdAt: z.string(),
});
export type ApprovalInstanceStep = z.infer<typeof ApprovalInstanceStepSchema>;

export const ApproverRowSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  instanceId: z.string().uuid(),
  stepId: z.string().uuid(),
  userId: z.string().uuid(),
  status: z.enum(APPROVER_STATUSES),
  reason: z.string().nullable(),
  decidedAt: z.string().nullable(),
  createdAt: z.string(),
});
export type ApproverRow = z.infer<typeof ApproverRowSchema>;

/** Body for a single reject — a reason is always required. Approve takes
 *  no body. */
export const RejectInput = z.object({
  reason: z.string().min(1).max(1000),
});
export type RejectInput = z.infer<typeof RejectInput>;

export const CancelInput = z.object({
  reason: z.string().max(1000).nullable().optional(),
});
export type CancelInput = z.infer<typeof CancelInput>;

export const BatchCheckInput = z.object({
  objectType: z.string().min(1).max(64),
  objectIds: z.array(z.string().uuid()).min(1).max(100),
});
export type BatchCheckInput = z.infer<typeof BatchCheckInput>;
